import { useQuery, UseQueryResult } from "@tanstack/react-query";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import { ServiceProviderType } from "./ServiceProviderService";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export type TestingServiceProviderStatus = "DONE" | "ERROR" | "PENDING";

export interface ITestingServiceProvider {
  id: number;
  serviceProviderId: number;
  destinationNumber: string;
  response: string;
  cost?: number;
  operatorId?: number;
  countryId?: number;
  type: ServiceProviderType;
  status: TestingServiceProviderStatus;
  createdBy?: string;
  updatedBy?: string;
  createdDate?: string;
}

export interface IExecuteTestParams {
  serviceProviderId: number;
  msisdn: string;
}

export interface IExecuteTestResponse {
  id: number;
  serviceProviderId: number;
  destinationNumber: string;
  response: string;
  cost?: number;
  operatorId?: number;
  countryId?: number;
  type: ServiceProviderType;
  status: TestingServiceProviderStatus;
  createdBy?: string;
  createdDate?: string;
}

// Fetch all testing service providers with pagination
export async function fetchTestingServiceProviders(
  params: Params<ITestingServiceProvider>
): Promise<IPageable<ITestingServiceProvider>> {
  try {
    const response = await axiosInstance.get("/testing-service-providers", { params });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching testing service providers:", error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching testing service providers."
    );
  }
}

// Fetch all testing service providers (no pagination)
export async function fetchAllTestingServiceProviders(
  filter?: string
): Promise<ITestingServiceProvider[]> {
  try {
    const response = await axiosInstance.get("/testing-service-providers/all", {
      params: filter ? { filter } : {},
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching all testing service providers:", error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching all testing service providers."
    );
  }
}

// Get testing service provider by ID
export async function getTestingServiceProviderById(
  id: number | string
): Promise<ITestingServiceProvider> {
  try {
    const response = await axiosInstance.get(`/testing-service-providers/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the testing service provider."
    );
  }
}

// Fetch history for a specific testing service provider
export async function fetchTestingServiceProviderHistory(
  mainId: number,
  params: Params<ITestingServiceProvider> = {}
): Promise<IPageable<ITestingServiceProvider>> {
  try {
    const response = await axiosInstance.get(`/testing-service-providers/${mainId}/history`, {
      params: { sort: "createdDate,desc", ...params },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while fetching testing history for service provider ID ${mainId}.`
    );
  }
}

// Fetch all histories (paginated)
export async function fetchAllTestingHistories(
  params: Params<ITestingServiceProvider> & { fromDate?: string; toDate?: string } = {}
): Promise<IPageable<ITestingServiceProvider>> {
  try {
    const response = await axiosInstance.get("/testing-service-providers/history", {
      params: { sort: "createdDate,desc", ...params },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching testing histories."
    );
  }
}

// Execute test
export async function executeServiceProviderTest(
  params: IExecuteTestParams
): Promise<IExecuteTestResponse> {
  try {
    const response = await axiosInstance.post("/testing-service-providers/execute", null, {
      params: {
        serviceProviderId: params.serviceProviderId,
        msisdn: params.msisdn,
      },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while executing the service provider test."
    );
  }
}

// Fetch testing history for a specific service provider (by serviceProviderId filter)
export async function fetchTestingHistoryByServiceProviderId(
  serviceProviderId: number,
  params: Params<ITestingServiceProvider> = {}
): Promise<IPageable<ITestingServiceProvider>> {
  try {
    const response = await axiosInstance.get("/testing-service-providers", {
      params: { 
        serviceProviderId, 
        sort: "createdDate,desc", 
        ...params 
      },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while fetching testing history for service provider ID ${serviceProviderId}.`
    );
  }
}

// React Query hook for testing service providers
export const useTestingServiceProvidersQuery = (
  params: Params<ITestingServiceProvider>
): UseQueryResult<IPageable<ITestingServiceProvider>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["testingServiceProviders", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchTestingServiceProviders(effectiveParams),
  });
};

// React Query hook for testing history by service provider ID
export const useTestingHistoryByServiceProviderIdQuery = (
  serviceProviderId: number,
  params: Params<ITestingServiceProvider> = {}
): UseQueryResult<IPageable<ITestingServiceProvider>, Error> => {
  const effectiveParams = {
    ...params,
    serviceProviderId,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["testingServiceProviderHistory", serviceProviderId, JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchTestingHistoryByServiceProviderId(serviceProviderId, params),
    enabled: !!serviceProviderId,
  });
};
